import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
// @mui
import {
  Avatar,
  Box,
  Button,
  Divider,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// utils

// components
import { LoadingButton } from '@mui/lab';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { organizationApi } from 'src/api/organization';
import { visitApi } from 'src/api/visit';
import FormProvider, { RHFAutocomplete } from 'src/components/hook-form';
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { Categories } from 'src/types/User';
import { GetVisitById, PaymentStatusEnum, ProfessionalSlotsResponseDto } from 'woofwoof-api';
import VisitCalendar from '../../../visit/components/VisitCalendar';
import VisitCalendarSkeleton from '../../../visit/components/VisitCalendarSkeleton';
import { translateKey } from 'src/locales/translate';

// ----------------------------------------------------------------------

export type OrganizationVisitModalEditTabFormValuesProps = {
  professionalId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  date: string;
};

type OrganizationVisitModalEditTabProps = {
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  onClose: () => void;
  visit: GetVisitById;
  isOpen: boolean;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
};

export default function OrganizationVisitModalEditTab({
  onClose,
  visit,
  isOpen,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
}: OrganizationVisitModalEditTabProps) {
  const { selectedOrganization } = useOrganizationContext();
  const isPaymentPending =
    visit?.payment?.status === PaymentStatusEnum.Created;
  const theme = useTheme();
  const { isMd, isTabletMd, isTabletSm } = useResponsiveBreakpoints();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDown515Px = useMediaQuery((theme: any) =>
    theme.breakpoints.down(515),
  );

  const getNumOfColumnsForDisplaySize = () => {
    if (isDown515Px) {
      return 3;
    }
    if (isMd) {
      return 4;
    }
    if (isTabletSm) {
      return 5;
    }
    if (isTabletMd) {
      return 6;
    } else {
      return 6;
    }
  };

  const UpdateUserSchema: Yup.Schema<OrganizationVisitModalEditTabFormValuesProps> =
    Yup.object().shape({
      professionalId: Yup.string().required(translateKey("validation.professionalRequired")),
      firstName: Yup.string().required(translateKey("visit.validationNameRequired")),
      lastName: Yup.string().required(translateKey("validation.lastNameRequired")),
      avatarUrl: Yup.string(),
      date: Yup.string().required('Data jest wymagana'),
    });

  const { data: professionalsData, isLoading: isLoadingProfessionals } =
    useQuery(
      ['schedule-search-professionals', selectedOrganization?.id],
      () =>
        organizationApi.organizationControllerGetOrganizationProfessionals({
          id: selectedOrganization?.id || '',
        }),
      {
        enabled: isOpen,
        retry: 2,
        staleTime: 5 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
      },
    );

  const defaultValues = {
    professionalId: visit?.professional?.id || '',
    firstName: visit?.professional?.firstName || '',
    lastName: visit?.professional?.lastName || '',
    avatarUrl: visit?.professional?.avatar || '',
    date: visit?.startTime || '',
  };

  const methods = useForm<OrganizationVisitModalEditTabFormValuesProps>({
    resolver: yupResolver(UpdateUserSchema),
    defaultValues,
  });

  const { handleSubmit, setValue, watch, formState } = methods;
  const watchedProfessionalId = watch('professionalId');
  const watchedDate = watch('date');

  const { data: slotsData, isLoading: isLoadingSlots } = useQuery(
    [
      'schedule-available-slots',
      watchedProfessionalId,
      selectedOrganization?.id,
      visit?.service?.duration,
    ],
    () =>
      visitApi.visitControllerGetProfessionalAvailableSlots({
        professionalId: watchedProfessionalId || '',
        category: Categories.veterinarian,
        organizationId: selectedOrganization?.id,
        duration: visit?.service?.duration,
      }),
    {
      enabled: !!watchedProfessionalId && isOpen,
      cacheTime: 0,
      retry: 2,
      select: (data: AxiosResponse<ProfessionalSlotsResponseDto>) => {
        return data.data.slots.map((slot: any) => ({
          date: new Date(slot),
          available: true,
        }));
      },
    },
  );

  const professionalsOptions = professionalsData?.data?.map(
    (professional: any) => ({
      value: professional.id,
      label: `${professional.firstName} ${professional.lastName}`,
      avatarUrl: professional.avatar,
    }),
  );

  const handleOnSelect = useCallback(
    (date: Date) => {
      const selectedDateTime = date.toISOString();

      if (selectedDateTime === watchedDate) {
        setValue('date', '');
        return;
      }

      setValue('date', date.toISOString());
    },
    [watchedDate, setValue],
  );

  const selectedProfessionalOption = watchedProfessionalId
    ? professionalsOptions?.find(
        (option) => option.value === watchedProfessionalId,
      ) || null
    : null;

  const isFormDirty =
    watchedProfessionalId !== defaultValues.professionalId ||
    watchedDate !== defaultValues.date;

  return (
    <FormProvider
      methods={methods}
      // Fill the modal body as a flex column so the footer pins to the bottom
      // (the content area scrolls), matching the Szczegóły tab.
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      onSubmit={
        onReschedule &&
        handleSubmit((data) =>
          onReschedule({
            professionalId: data.professionalId,
            date: data.date,
            visitId: visit.id,
          }),
        )
      }
    >
      <Box
        rowGap={isMobile ? 2 : 3}
        display="grid"
        width="100%"
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        <Divider />
        <Stack sx={{ position: 'relative', gap: 2 }}>
          <RHFAutocomplete
            name="professionalOption"
            label={translateKey("search.book.cardSpecialist")}
            disabled
            // TODO: To ommit the issue when someone try to reschedule the visit to another specjalist who might not support that visit type or category
            // disabled={isLoadingProfessionals || isLoadingSlots}
            disableClearable
            options={professionalsOptions || []}
            defaultValue={professionalsOptions?.find(
              (option) => option.value === watchedProfessionalId,
            )}
            onChange={(event, newValue) => {
              const professional = professionalsData?.data?.find(
                (professional) => professional.id === newValue.value,
              );
              setValue('professionalId', professional?.id || '');
              setValue('firstName', professional?.firstName || '');
              setValue('lastName', professional?.lastName || '');
              setValue('avatarUrl', professional?.avatar || '');

              // Reset date when professional changes
              setValue('date', '');
              return `${professional?.firstName} ${professional?.lastName}`;
            }}
            getOptionLabel={(option) =>
              (option as { value: string; label: string; avatarUrl: string })
                .label || ''
            }
            value={selectedProfessionalOption as any}
            inputProps={{
              startAdornment: selectedProfessionalOption && (
                <InputAdornment position="start">
                  <Avatar
                    src={selectedProfessionalOption.avatarUrl}
                    alt={selectedProfessionalOption.label}
                    sx={{ width: 32, height: 32 }}
                  >
                    {selectedProfessionalOption.label?.charAt(0)}
                  </Avatar>
                </InputAdornment>
              ),
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Avatar
                  src={
                    (
                      option as {
                        value: string;
                        label: string;
                        avatarUrl?: string;
                      }
                    ).avatarUrl
                  }
                  alt={
                    (
                      option as {
                        value: string;
                        label: string;
                        avatarUrl?: string;
                      }
                    ).label
                  }
                  sx={{ width: 24, height: 24 }}
                >
                  {(
                    option as {
                      value: string;
                      label: string;
                      avatarUrl?: string;
                    }
                  ).label?.charAt(0)}
                </Avatar>
                <Typography variant="body2">
                  {
                    (
                      option as {
                        value: string;
                        label: string;
                        avatarUrl?: string;
                      }
                    ).label
                  }
                </Typography>
              </Box>
            )}
            isOptionEqualToValue={(option, value) =>
              (option as { value: string; label: string; avatarUrl: string })
                .value ===
              (value as { value: string; label: string; avatarUrl: string })
                .value
            }
          />
        </Stack>
        <Divider />
        <Stack sx={{ gap: 2, minHeight: '380px' }}>
          <Typography>{translateKey("visit.rescheduleVisitTitle")}</Typography>
          {isLoadingSlots || isLoadingProfessionals ? (
            <VisitCalendarSkeleton
              numberOfSkeletonRows={7}
              calendarColumns={getNumOfColumnsForDisplaySize()}
            />
          ) : (
            <VisitCalendar
              dates={slotsData || []}
              onSelect={handleOnSelect}
              disabled={
                isLoadingSlots || isLoadingProfessionals || isRescheduleLoading
              }
              isLoading={isLoadingSlots || isLoadingProfessionals}
              numberOfSkeletonRows={7}
              calendarColumns={getNumOfColumnsForDisplaySize()}
            />
          )}
        </Stack>
      </Box>

      <Stack
        gap={3}
        justifyContent={onCancel ? 'space-between' : 'flex-end'}
        sx={{ mt: 3, flexShrink: 0 }}
        flexDirection={isDown515Px ? 'column' : 'row'}
        width="100%"
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          fullWidth={isDown515Px}
          size={isDown515Px ? 'large' : 'medium'}
        >{translateKey("common.close")}</Button>

        <Stack gap={1.5} flexDirection={isDown515Px ? 'column' : 'row'}>
          {onCancel && (
            <LoadingButton
              color="error"
              variant="outlined"
              data-testid="visit-cancel-open"
              sx={{ mt: '0px !important' }}
              onClick={onCancel}
              loading={isCancelLoading}
              disabled={
                isLoadingSlots ||
                isLoadingProfessionals ||
                isRescheduleLoading ||
                isCancelLoading ||
                isPaymentPending
              }
              fullWidth={isDown515Px}
              size={isDown515Px ? 'large' : 'medium'}
            >
              <span>{translateKey("visit.cancelVisitShort")}</span>
            </LoadingButton>
          )}
          <LoadingButton
            variant="contained"
            sx={{ mt: '0px !important' }}
            type="submit"
            loading={isRescheduleLoading}
            disabled={
              isLoadingSlots ||
              isLoadingProfessionals ||
              !watchedDate ||
              isRescheduleLoading ||
              isCancelLoading ||
              !isFormDirty ||
              isPaymentPending
            }
            fullWidth={isDown515Px}
            size={isDown515Px ? 'large' : 'medium'}
          >
            <span>{translateKey("common.save")}</span>
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
